import React, { useCallback, useMemo } from "react"
import { State } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { addElement, getCountText, getDefaultContentLanguage, removeElementById } from "../../globalStates/utils"
import { hasServiceKeys, useConfigDataState } from "../../globalStates/ConfigDataState"
import { EMPTY_SHOWROOM, useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { addValidatorsShowroom } from "../../globalStates/ExhibitorValidation"
import { useLanguageState } from "../../globalStates/LanguageState"
import { newId } from "../../utils/Id"
import { repositionItem } from "../../utils/Arrays"
import Page, { Title, TitleWrapper, Description } from "../../components/Page"
import ShowroomsSection from "./ShowroomsSection"
import { Showrooms } from "../../globalStates/ExhibitorDataTypes"
import ButtonWithCaption from "../../components/ButtonWithCaption"

function sortShowrooms(showrooms: State<Showrooms, Validation>[]): State<Showrooms, Validation>[] {
    return showrooms.sort((p1, p2) => p1.orderShowrooms.get() - p2.orderShowrooms.get())
}

const ShowroomsPage: React.FC<{}> = () => {
    const branding = useLanguageState().getStrings()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const showroomsPublicLimit = serviceKeys.showroomPublic || 0
    const showroomsPrivateLimit = serviceKeys.showroomPrivate || 0
    const showroomsPublicVisible = serviceKeys.showroomPublic !== undefined && hasServiceKeys(serviceKeys.showroomPublic)
    const showroomsPrivateVisible = serviceKeys.showroomPrivate !== undefined && hasServiceKeys(serviceKeys.showroomPrivate)
    const showroomsStates = useExhibitorDataState().getShowrooms()
    const orderedShowroomsPublicStates = useMemo(
        () => sortShowrooms(showroomsStates.filter((showroomsState) => showroomsState.accessMethod.get() === "Pb")),
        [showroomsStates]
    )
    const orderedShowroomsPrivateStates = useMemo(
        () => sortShowrooms(showroomsStates.filter((showroomsState) => showroomsState.accessMethod.get() === "Br")),
        [showroomsStates]
    )

    const canAddShowroomsPublic = useMemo(() => {
        if (orderedShowroomsPublicStates.length >= showroomsPublicLimit) return false
        let valid = true
        orderedShowroomsPublicStates.forEach((showroomsState) => {
            const title = showroomsState.title.get()
            const titleDe = showroomsState.titleDe.get()

            getDefaultContentLanguage() === "De"
                ? (valid = valid && !!titleDe && titleDe.length >= 0)
                : (valid = valid && !!title && title.length >= 0)
        })
        return valid
    }, [showroomsPublicLimit, orderedShowroomsPublicStates])

    const canAddShowroomsPrivate = useMemo(() => {
        if (orderedShowroomsPrivateStates.length >= showroomsPrivateLimit) return false
        let valid = true
        orderedShowroomsPrivateStates.forEach((showroomsState) => {
            const title = showroomsState.title.get()
            const titleDe = showroomsState.titleDe.get()

            getDefaultContentLanguage() === "De"
                ? (valid = valid && !!titleDe && titleDe.length >= 0)
                : (valid = valid && !!title && title.length >= 0)
        })
        return valid
    }, [showroomsPrivateLimit, orderedShowroomsPrivateStates])

    const onAddShowroomsPublic = useCallback(() => {
        addElement<Showrooms>(showroomsStates, {
            ...EMPTY_SHOWROOM,
            id: newId().toString(),
            accessMethod: "Pb",
            orderShowrooms: orderedShowroomsPublicStates.length + 1
        })
        addValidatorsShowroom(showroomsStates[showroomsStates.length - 1])
    }, [orderedShowroomsPublicStates.length, showroomsStates])

    const onAddShowroomsPrivate = useCallback(() => {
        addElement<Showrooms>(showroomsStates, {
            ...EMPTY_SHOWROOM,
            id: newId().toString(),
            accessMethod: "Br",
            orderShowrooms: orderedShowroomsPrivateStates.length + 1
        })
        addValidatorsShowroom(showroomsStates[showroomsStates.length - 1])
    }, [orderedShowroomsPrivateStates.length, showroomsStates])

    const onRemoveShowroomsPublic = useCallback(
        (productId: string) => {
            removeElementById<Showrooms>(showroomsStates, productId)
        },
        [showroomsStates]
    )

    const onRemoveShowroomsPrivate = useCallback(
        (productId: string) => {
            removeElementById<Showrooms>(showroomsStates, productId)
        },
        [showroomsStates]
    )

    const onOrderShowroomsPublicChanged = useCallback(
        (productId: string, newIndex: number) => {
            const oldIndex = orderedShowroomsPublicStates.findIndex((showroomsStates) => showroomsStates.id.get() === productId)
            const repositionedShowroomsStates = repositionItem(
                orderedShowroomsPublicStates,
                orderedShowroomsPublicStates[oldIndex],
                oldIndex,
                newIndex
            )
            repositionedShowroomsStates.forEach((showrooms, index) => {
                if (showrooms.orderShowrooms.get() !== index + 1) showrooms.orderShowrooms.set(index + 1)
            })
        },
        [orderedShowroomsPublicStates]
    )

    const onOrderShowroomsPrivateChanged = useCallback(
        (productId: string, newIndex: number) => {
            const oldIndex = orderedShowroomsPrivateStates.findIndex((showroomsStates) => showroomsStates.id.get() === productId)
            const repositionedShowroomsStates = repositionItem(
                orderedShowroomsPrivateStates,
                orderedShowroomsPrivateStates[oldIndex],
                oldIndex,
                newIndex
            )
            repositionedShowroomsStates.forEach((showrooms, index) => {
                if (showrooms.orderShowrooms.get() !== index + 1) showrooms.orderShowrooms.set(index + 1)
            })
        },
        [orderedShowroomsPrivateStates]
    )

    return (
        <Page description="">
            {showroomsPublicVisible && (
                <>
                    <TitleWrapper>
                        <Title>{branding.configuration.showroomsPage.pageTitlePublic}</Title>
                    </TitleWrapper>
                    <Description>{branding.configuration.showroomsPage.pageDescriptionPublic}</Description>
                    {orderedShowroomsPublicStates?.map((showroomsState, index) => (
                        <ShowroomsSection
                            key={showroomsState.id.get()}
                            type="public"
                            showroomsState={showroomsState}
                            index={index}
                            onOrderChanged={onOrderShowroomsPublicChanged}
                            onClickRemove={onRemoveShowroomsPublic}
                        />
                    ))}

                    <ButtonWithCaption
                        id="addShowroomPublic"
                        caption={
                            branding.showCounter
                                ? getCountText(orderedShowroomsPublicStates.length, serviceKeys.showroomPublic || 0)
                                : undefined
                        }
                        buttonText={branding.configuration.showroomsPage.btnAddShowrooms}
                        onClick={onAddShowroomsPublic}
                        disabled={!canAddShowroomsPublic}
                    />
                </>
            )}
            <br />
            {showroomsPrivateVisible && (
                <>
                    <TitleWrapper>
                        <Title>{branding.configuration.showroomsPage.pageTitlePrivate}</Title>
                    </TitleWrapper>
                    <Description>{branding.configuration.showroomsPage.pageDescriptionPrivate}</Description>
                    {orderedShowroomsPrivateStates?.map((showroomsState, index) => (
                        <ShowroomsSection
                            key={showroomsState.id.get()}
                            type="private"
                            showroomsState={showroomsState}
                            index={index}
                            onOrderChanged={onOrderShowroomsPrivateChanged}
                            onClickRemove={onRemoveShowroomsPrivate}
                        />
                    ))}

                    <ButtonWithCaption
                        id="addShowroomPublic"
                        caption={
                            branding.showCounter
                                ? getCountText(orderedShowroomsPrivateStates.length, serviceKeys.showroomPrivate || 0)
                                : undefined
                        }
                        buttonText={branding.configuration.showroomsPage.btnAddShowrooms}
                        onClick={onAddShowroomsPrivate}
                        disabled={!canAddShowroomsPrivate}
                    />
                </>
            )}
        </Page>
    )
}

export default ShowroomsPage
